import React, { useEffect, useMemo } from 'react';

import { UseQueryResult } from '@tanstack/react-query';

import { Alert, CircularProgress, Stack, Typography } from '@mui/material';

import { GetRateCodes, GetRateCodesResponse } from '~/services/cruises/RateCodeService';

import VendorTab, { Vendor } from './VendorTab';

interface Props {
  rateCodesQuery: UseQueryResult<GetRateCodesResponse, Error>;
  selectedVendor: Vendor | null;
  onSelectVendor: (vendor: Vendor) => void;
}

function groupRatesByVendor(rates: Array<GetRateCodes>): Array<Vendor> {
  const vendorsById = new Map<string, Vendor>();

  for (const rate of rates) {
    const vendor = vendorsById.get(rate.vendor.id) ?? {
      id: rate.vendor.id,
      name: rate.vendor.name,
      rates: [],
    };

    vendor.rates.push({ code: rate.code });
    vendorsById.set(vendor.id, vendor);
  }

  return Array.from(vendorsById.values());
}

function VendorTabSection({ rateCodesQuery, selectedVendor, onSelectVendor }: Props) {
  const vendors = useMemo(() => groupRatesByVendor(rateCodesQuery.data?.result ?? []), [rateCodesQuery.data]);
  const selectedVendorId = selectedVendor?.id ?? vendors[0]?.id;

  useEffect(() => {
    if (!selectedVendor && vendors[0]) {
      onSelectVendor(vendors[0]);
    }
  }, [onSelectVendor, selectedVendor, vendors]);

  if (rateCodesQuery.isLoading) {
    return <CircularProgress aria-label="Loading rate codes" />;
  }

  if (rateCodesQuery.isError) {
    return <Alert severity="error">Unable to load rate codes.</Alert>;
  }

  if (!vendors.length || !selectedVendorId) {
    return <Typography>No rate codes found.</Typography>;
  }

  return (
    <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1 }}>
      {vendors.map((vendor) => (
        <VendorTab
          key={vendor.id}
          vendor={vendor}
          selected={vendor.id === selectedVendorId}
          onSelect={() => onSelectVendor(vendor)}
        />
      ))}
    </Stack>
  );
}

export default VendorTabSection;
